import React from 'react';

import { DeliveredStatus } from '@amzn/aet-coachingform-kit/lib/types';
import { DELIVERY_STATUS_LABEL } from '../../Table/LighthouseMetrics/displayTypes';
import { FormattedMessage } from 'react-intl';
import {
  Trend,
  calculateSwitch,
  MetricsOption,
  VerticalOption,
} from '@amzn/aet-galactus-kit';
import { checkGoUser } from '../../../../Util/User';
import { Feature, isFeatureEnabled } from '../../../../config/FeaturesConfig';
import { filterCCXMetricsForRemoval } from '../../Table/TimeTrends/helper';

const TIME_FORMAT_METRICS = ['HOLD_TIME'];
export const NO_DATA_LABEL = 'N/A';

export const getBQMetricsList = () =>
  filterCCXMetricsForRemoval([
    isFeatureEnabled(Feature.CCX_BOTTOM_TWO_BOX)
      ? MetricsOption.CCX_NEG
      : MetricsOption.CCX_NEG_ONEBOX,
    MetricsOption.RAP,
    MetricsOption.HOLD_TIME,
    MetricsOption.HIGH_SILENCE_RATE,
  ]);

export const coachingStatusLabels = (
  coachingStatus: DeliveredStatus
): React.ReactElement => {
  const statusLabel = DELIVERY_STATUS_LABEL[coachingStatus];
  if (statusLabel) {
    return (
      <FormattedMessage
        id={statusLabel.id}
        defaultMessage={statusLabel.default}
      />
    );
  }
  return <>--</>;
};

export const formatMonthMetrics = (metricData: Trend) => {
  const formattedValues = metricData?.trendDatapoint?.flatMap(trendDataObj => {
    if (trendDataObj.datapoint) {
      return {
        metric: metricData.name,
        value: trendDataObj.datapoint!,
        netAvg: trendDataObj.networkAverage ?? undefined,
      };
    } else {
      return {
        metric: metricData.name,
        value: undefined,
        netAvg: undefined,
      };
    }
  });
  return formattedValues;
};

export const formatPerformanceSummary = (metricData: Trend) => {
  const trendData = metricData?.trendDatapoint?.flatMap(trendDataObj => {
    if (trendDataObj.datapoint) {
      if (trendDataObj.datapoint.includes(':')) {
        const [minutes, seconds] = trendDataObj.datapoint
          .split(':')
          .map(Number);
        return minutes * 60 + seconds;
      } else {
        return parseFloat(trendDataObj.datapoint);
      }
    }
    return null;
  });

  if (trendData && trendData.length > 0) {
    let startValue = trendData.find(data => data !== null) || 0;
    let endValue = trendData[trendData.length - 1];
    const lastValue = metricData.trendDatapoint.at(-1)?.datapoint || '';
    const formattedLastValue = !isNaN(Number(lastValue))
      ? (parseFloat(lastValue) * 100).toFixed(1)
      : lastValue.toString();
    let deltaVal = calculateSwitch(startValue, endValue, metricData.name, 2);
    let adjustedDeltaVal;

    if (
      typeof endValue === 'number' &&
      Number.isInteger(endValue) &&
      deltaVal !== null &&
      TIME_FORMAT_METRICS.includes(metricData.name)
    ) {
      const minutes = Math.floor(Math.abs(deltaVal) / 60);
      const seconds = Math.abs(deltaVal) % 60;
      const sign = Math.sign(deltaVal) === -1 ? '-' : '';
      adjustedDeltaVal = `${sign}${minutes}:${seconds
        .toString()
        .padStart(2, '0')}s`;
    } else if (typeof endValue === 'number' && deltaVal !== null) {
      const deltaInPercentage = deltaVal * 100;
      let formattedNumber = deltaInPercentage.toFixed(1);
      adjustedDeltaVal = `${formattedNumber}%`;
    }

    return {
      metric: metricData.name,
      delta: adjustedDeltaVal ?? null,
      trendIndicator:
        deltaVal !== null && Math.sign(deltaVal) === -1
          ? 'negative'
          : deltaVal !== null && Math.sign(deltaVal) === 1
            ? 'positive'
            : 'neutral',
      endValue: `${formattedLastValue}`,
    };
  } else {
    return [];
  }
};

export const filterMetrics = (
  owner: string,
  vertical: string
): MetricsOption[] => {
  if (
    vertical === VerticalOption.Consumer ||
    vertical === VerticalOption.CAP ||
    vertical === VerticalOption.Total
  ) {
    if (owner && !checkGoUser(owner)) {
      return getBQMetricsList().filter(metric => metric !== MetricsOption.RAP);
    }
  }
  return getBQMetricsList();
};
